'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import { v4 as uuid } from "uuid";
import { useTranslations } from 'next-intl';
import { FiX } from 'react-icons/fi';
import { RiRedPacketLine } from 'react-icons/ri';
import { TbTruckDelivery } from 'react-icons/tb';
import { usePathname, useRouter } from 'next/navigation';

import styles from './service-modal.module.scss';

import { alertService } from '_services/alert.service';
import { AvailableSlot, CompanyDetails, DayOfWeek, formatCurrency, getAvailableTimeSlotsByType, OpeningHour, rearrangeOpeningHours, SlotType, TimeRangeConfig, timezoneDateConvert } from '_services/common.service';
import { OrderServeDetails } from '@store/slices/orderActions';

import useStore from '@store/index';
import { CommonResponse, submitCollection, submitSets } from '_services/submit.service';
import { loadingService } from '_services/loading.service';
import { debounce, DebouncedFunc } from 'lodash';
import ModalWrapperSheet from './cart-success-sheet';
import cartsuccessstyles from "../modals/cart-success-sheet.module.scss"
import { AiOutlineThunderbolt } from 'react-icons/ai';

type DeliveryType = 'PICKUP' | 'DELIVERY'; // "SHIPMENT"
type TimeType = 'ASAP' | 'SCHEDULED';
type RangeItem = {
    rangeStart?: number;
    rangeEnd?: number;
    amount?: number;
    currency?: string;
    rangeUnit?: string;
};

export const ServiceModal = ({ 
    isManualShow, 
    onToggleManualShow,
    isCompanyOpen,
    currentDate,
    sendingCompanyDetails
}: { 
    isManualShow: boolean, 
    onToggleManualShow: () => void,
    isCompanyOpen: boolean | null,
    currentDate: Date | null,
    sendingCompanyDetails?: CompanyDetails | null
}) => {
    const pathname = usePathname();
    const router = useRouter();
    const t = useTranslations();

    const isPickupEnabled = true; //companyDetails?.isPickupEnabled ?? true;
    const isDeliveryEnabled = true; //companyDetails?.isDeliveryEnabled ?? true;
   
    const [showServiceModal, setShowServiceModal] = React.useState(false);

    const [selectedService, setSelectedService] = useState<DeliveryType | null>('PICKUP');
    const [selectedTime, setSelectedTime] = useState<TimeType>('SCHEDULED');
    const [orderDate, setOrderDate] = useState<Date | null>(null);
    const [orderTime, setOrderTime] = useState<string | Date>('');
    
    const [isAfterClose, setIsAfterClose] = useState(false);
    const [isNoSlotsAvailableStatus, setIsNoSlotsAvailableStatus] = useState(false);
    const [isOnload, setIsOnload] = useState(true);
    const [currentServerTime, setCurrentServerTime] = useState<Date | null>(null);

    const [localCompanyDetails, setLocalCompanyDetails] = useState<CompanyDetails | null>(null);
    const [availableDates, setAvailableDates] = useState<Date[]>([]);

    const [isShowCartClearConfirm, setIsShowCartClearConfirm] = useState(false);
    const [isSchedulePassed, setIsSchedulePassed] = useState(false);

    const [isFutureScheduleClosed, setIsFutureScheduleClosed] = useState(false);
    const [selectedScheduleTime, setSelectedScheduleTime] = useState('');

    const [currentCompanyOpen, setCurrentCompanyOpen] = useState<boolean | null>(null);

    const [isPickerTimesLoading, setIsPickerTimesLoading] = useState(false);

    const [showScheduleTimeErrorSheet, setShowScheduleTimeErrorSheet] = useState(false);
    const [availableScheduleTimes, setAvailableScheduleTimes] = useState<Date[]>([]);

    const [isPreventLoadTimes, setIsPreventLoadTimes] = useState(false);
    
    const [isCompDetailsLoading, setIsCompDetailsLoading] = useState(true);

    // const [prevOrderClearConfirm, setPrevOrderClearConfirm] = useState(false);
    const pickerRef = React.useRef<DatePicker>(null);
    const datePickerRef = React.useRef<HTMLInputElement>(null);
    const timePickerRef = React.useRef<HTMLInputElement>(null);

    const [isSelectedDayClosed, setIsSelectedDayClosed] = useState(false);
    const timeGap = 15; //minutes

    // Get global states and actions from the store
    const { 
        orderServeDetails, setOrderServeDetails, 
        identityToken, setIdentityToken, setCompanyDetails,
        hasHydrated, cart, clearCart, isOnlineHold, companyDetails,setIsServiceModalOpen
    } = useStore();

    useEffect(() => {
        const isOpen = !isOnlineHold && ((!isShowCartClearConfirm && isManualShow) || showServiceModal);
        setIsServiceModalOpen(isOpen);
    }, [isOnlineHold, isShowCartClearConfirm, isManualShow, showServiceModal, setIsServiceModalOpen]);

    // Load company details on load
    const didMountRef = React.useRef(false);

    type AcceptanceStatus = 'ok' | 'hurry' | 'closed';
    const [pickupAcceptanceStatus, setPickupAcceptanceStatus] = useState<AcceptanceStatus>('ok');
    const [deliveryAcceptanceStatus, setDeliveryAcceptanceStatus] = useState<AcceptanceStatus>('ok');
    const HURRY_UP_GAP_MINUTES = 15;

    const getOrderMethod = async () => {

        // Make API call to update company details
        loadingService.loading(true);

        const response = await submitSets(
            submitCollection.getCompanyDetails, 
            { locationId: localCompanyDetails?.locationId || null }, 
            false, 
            null
        );

        loadingService.loading(false);

        let updatedCompanyDetails: CompanyDetails | null = null;

        if (response && response.status && response.extra) {
            updatedCompanyDetails = response.extra as CompanyDetails;
            setLocalCompanyDetails(updatedCompanyDetails);
            setCompanyDetails(updatedCompanyDetails);
        }

        const defaultMaxCounts = updatedCompanyDetails?.defaultMaxCounts;

        if (!defaultMaxCounts || defaultMaxCounts.globalMaxCount === 0) {
            return 'none';
        }

        const {globalMaxCount = 0, deliveryMaxCount = 0, pickupMaxCount = 0 } = defaultMaxCounts;

        // If globalMaxCount exists and is non-zero, it applies to both methods
        if (globalMaxCount > 0) {
            const noDelivery = deliveryMaxCount === 0;
            const noPickup = pickupMaxCount === 0;

            if (noDelivery && noPickup) {
                return 'both';
            }
            if (noDelivery) {
                return 'delivery';
            }
            if (noPickup) {
                return 'pickup';
            }
        }

        // Fallback when globalMaxCount is 0
        if (deliveryMaxCount === 0 && pickupMaxCount === 0) {
            return 'both';
        }

        if (deliveryMaxCount === 0) {
            return 'delivery';
        }

        if (pickupMaxCount === 0) {
            return 'pickup';
        }

        return 'none';
    };

    const fetchCompanyDetails = useCallback(async () => {
        setIsCompDetailsLoading(true);

        const response = (sendingCompanyDetails? {
            status: true,
            extra: sendingCompanyDetails
        } : await submitSets(submitCollection.getCompanyDetails, { locationId: null }, false, null));

        setIsCompDetailsLoading(false);
        
        if(response && response.status && response.extra){
            const companyDetails = response.extra as CompanyDetails;

            setLocalCompanyDetails(companyDetails);

            // Available future dates between today and next 7 days
            const companyOpenDays = (companyDetails?.additionalInfo?.openingHours || []).filter(day => day.isOpen).map(day => day.day.toLowerCase());

            let isCompanyOpened = isCompanyOpen;

            const serverTime = new Date((timezoneDateConvert({
                utcDate: (companyDetails.serverTime || ''),
                timezone: companyDetails.timeZone,
                format: 'YYYY-MM-DD HH:mm:ss'
            }) || new Date()));

            // Generate available dates based on company open days
            const futureDates: Date[] = [];
            for (let i = 0; i < 7; i++) {
                const futureDate = new Date(serverTime);
                const updatedFutureDate = new Date(futureDate.setDate(futureDate.getDate() + i));

                const dayName = timezoneDateConvert({ utcDate: updatedFutureDate.toISOString(), timezone: companyDetails.timeZone, format: 'ddd' });

                if (companyOpenDays.includes(dayName.toLowerCase())) {
                    futureDates.push(updatedFutureDate);
                }
            }
            setAvailableDates(futureDates);

            const isShopOpen = companyDetails?.shopStatus === 'OPEN';
            isCompanyOpened = isShopOpen;
            
            setCurrentCompanyOpen(isCompanyOpened);
            setCompanyDetails(companyDetails);
            setCurrentServerTime(serverTime);

            // const isShopOpen = await checkCompanyOpened(companyDetails);
            // console.log("Is shop open:", isShopOpen);
            setSelectedTime(isShopOpen ? 'ASAP' : 'SCHEDULED');

            const currentDay = serverTime.toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as DayOfWeek;
            const currentHour = serverTime.getHours();
            const currentMinute = serverTime.getMinutes();

            // const openingHours = (companyDetails?.additionalInfo?.openingHours || []).find((day) => day.isOpen && day.day.toLowerCase() === currentDay.substring(0, 3)) as OpeningHour | undefined;
            // Rearrange opening hours by day and convert to timezone
            const reArrangedOpeningHours = rearrangeOpeningHours(
                companyDetails?.additionalInfo?.openingHours || [],
                companyDetails?.timeZone
            );
            // find current day opening hours
            const openingHours = (reArrangedOpeningHours || []).find((day) => day.isOpen && day.day.toLowerCase() === (currentDay as string).substring(0, 3)) as OpeningHour | undefined;
            
            // console.log(openingHours);
            
            if(openingHours){
                // Use local time to avoid timezone offset issues
                let convertedCloseTime = timezoneDateConvert({
                    utcDate: `${serverTime.getFullYear()}-${(serverTime.getMonth()+1).toString().padStart(2, '0')}-${serverTime.getDate().toString().padStart(2, '0')}`,
                    timezone: (localCompanyDetails?.timeZone || ''),
                    format: 'HH:mm'
                });
    
                (openingHours?.openingHours || []).sort((a, b) => {
                    const aStart = timezoneDateConvert({ utcDate: (a.startTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });
                    const bStart = timezoneDateConvert({ utcDate: (b.startTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });
                    return (aStart < bStart ? -1 : 1);
                }).forEach((hourRange, index) => {
                    if((index + 1) === (openingHours?.openingHours || []).length){
                        convertedCloseTime = timezoneDateConvert({ utcDate: (hourRange.endTime || ''), timezone: (localCompanyDetails?.timeZone || ''), format: 'HH:mm' })
                    }
                });
    
                const [closeHour, closeMinute] = (convertedCloseTime || '').split(" ")[0].split(':').map(Number);

                // Create Date objects for proper comparison
                const now = new Date();
                const currentDate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    currentHour,
                    currentMinute
                );

                const closeDate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    closeHour,
                    closeMinute
                );

                // If closing hour is very early (e.g., 00:00-05:00) and current hour is late (e.g., 20:00-23:59),
                // it means closing is next day
                if (closeHour < 6 && currentHour > 18) {
                    closeDate.setDate(closeDate.getDate() + 1);
                }
                
                // Check if current time is after closing
                // const isAfterClose = (currentHour > closeHour || (currentHour === closeHour && currentMinute >= closeMinute));
                const isAfterClose = currentDate >= closeDate;
                setIsAfterClose(isAfterClose);
            }
            
            if (orderServeDetails) {
                const currentOrderDetails = orderServeDetails;
                // console.log("Current order details:", currentOrderDetails);
            
                // Check if delivery scheduled time has already passed
                if(
                    (currentOrderDetails.fullFillType === "DELIVERY" && 
                    currentOrderDetails.deliveryDetails && 
                    currentOrderDetails.deliveryDetails.scheduleType === "SCHEDULED" &&
                    currentOrderDetails.deliveryDetails.deliveryAt !== '') ||

                    (currentOrderDetails.fullFillType === "PICKUP" && 
                    currentOrderDetails.pickupDetails && 
                    currentOrderDetails.pickupDetails.scheduleType === "SCHEDULED" &&
                    currentOrderDetails.pickupDetails.pickupAt !== '')
                ) {
                    const scheduledAt = new Date(currentOrderDetails.fullFillType === "DELIVERY" ? (currentOrderDetails?.deliveryDetails?.deliveryAt || '') : (currentOrderDetails?.pickupDetails?.pickupAt || ''));

                    // Compare scheduled time with new open hours to see if it's still valid
                    const scheduledDay = scheduledAt.toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as DayOfWeek;
                    const scheduledOpeningHours = (reArrangedOpeningHours || []).find((day) => day.isOpen && day.day.toLowerCase() === scheduledDay.substring(0, 3)) as OpeningHour | undefined;
                    // const scheduledOpeningHours = (companyDetails?.additionalInfo?.openingHours || []).find((day) => day.isOpen && day.day.toLowerCase() === scheduledDay.substring(0, 3)) as OpeningHour | undefined;

                    if(scheduledOpeningHours){
                        let isWithinOpenHours = false;
                        (scheduledOpeningHours?.openingHours || []).forEach((hourRange) => {
                            // Use local time to avoid timezone offset issues
                            const convertedOpenTime = timezoneDateConvert({ utcDate: (hourRange?.startTime || ''), timezone: (localCompanyDetails?.timeZone || ''), format: 'HH:mm' });
                            const convertedCloseTime = timezoneDateConvert({ utcDate: (hourRange?.endTime || ''), timezone: (localCompanyDetails?.timeZone || ''), format: 'HH:mm' });
                            const [openHour, openMinute] = (convertedOpenTime || '').split(':').map(Number);
                            const [closeHour, closeMinute] = (convertedCloseTime || '').split(':').map(Number);
                            if(
                                (scheduledAt.getHours() > openHour || (scheduledAt.getHours() === openHour && scheduledAt.getMinutes() >= openMinute)) &&
                                (scheduledAt.getHours() < closeHour || (scheduledAt.getHours() === closeHour && scheduledAt.getMinutes() < closeMinute))
                            ){
                                isWithinOpenHours = true;
                            }
                        });

                        if(!isWithinOpenHours){
                            setIsSchedulePassed(true);
                            setIsFutureScheduleClosed(true);
                            setSelectedScheduleTime(timezoneDateConvert({ utcDate: scheduledAt.toISOString(), timezone: (localCompanyDetails?.timeZone || ''), format: 'YYYY-MM-DD HH:mm a' }).toString());
                            loadingService.loading(false);
                            
                            if(cart && cart.lineItems && cart.lineItems.length > 0){
                                setIsShowCartClearConfirm(true);
                                setShowServiceModal(false);
                            } else{
                                setOrderServeDetails(null);
                                clearCart();
                                setShowServiceModal(true);
                            }
                            return;
                        }
                    }

                    // before current server time
                    if (scheduledAt < serverTime) {
                        setIsSchedulePassed(true);
                        loadingService.loading(false);

                        if(cart && cart.lineItems && cart.lineItems.length > 0){
                            setIsShowCartClearConfirm(true);
                            setShowServiceModal(false);
                        } else{
                            setOrderServeDetails(null);
                            clearCart();
                            setShowServiceModal(true);
                        }

                        return;
                    }
                }

                // If shop is closed and order is ASAP, clear order details
                if (
                    !isShopOpen && 
                    ((currentOrderDetails.fullFillType === "DELIVERY" && currentOrderDetails.deliveryDetails && currentOrderDetails.deliveryDetails.scheduleType === "ASAP") ||
                    (currentOrderDetails.fullFillType === "PICKUP" && currentOrderDetails.pickupDetails && currentOrderDetails.pickupDetails.scheduleType === "ASAP"))
                ) {
                    setIsSchedulePassed(false);
                    loadingService.loading(false);

                    if(cart && cart.lineItems && cart.lineItems.length > 0){
                        setIsShowCartClearConfirm(true);
                        setShowServiceModal(false);
                    } else{
                        setOrderServeDetails(null);
                        clearCart();
                        setShowServiceModal(true);
                    }

                    // if(cart.orderId){
                    //     setPrevOrderClearConfirm(true);
                    // }

                    return;
                }

                // If fullfilledAt is set, check if it's not today
                if (currentOrderDetails && currentOrderDetails.setAt) {
                    const setAt = new Date(currentOrderDetails.setAt);

                    //check not today
                    if (setAt.toDateString() !== serverTime.toDateString()) {
                        setOrderServeDetails(null);
                        clearCart();
                        setShowServiceModal(true);

                        // if(cart.orderId){
                        //     setPrevOrderClearConfirm(true);
                        // }
                    }
                }

                // If ASAP order is not today, clear order details
                if (
                    ((currentOrderDetails.fullFillType === "DELIVERY" && currentOrderDetails.deliveryDetails && currentOrderDetails.deliveryDetails.scheduleType === "ASAP") ||
                    (currentOrderDetails.fullFillType === "PICKUP" && currentOrderDetails.pickupDetails && currentOrderDetails.pickupDetails.scheduleType === "ASAP")) &&
                    (new Date(currentOrderDetails.setAt).toDateString() !== serverTime.toDateString())
                ) {
                    setOrderServeDetails(null);
                    clearCart();
                    setShowServiceModal(true);
                    loadingService.loading(false);

                    // if(cart.orderId){
                    //     setPrevOrderClearConfirm(true);
                    // }

                    return;
                }

                // console.log("Order serve details are valid.");
                // setShowServiceModal(false);

                if(isManualShow){
                    if(companyDetails?.isDeliveryOn){
                        setSelectedService((orderServeDetails?.fullFillType?.toUpperCase?.() === 'DELIVERY' ? 'DELIVERY' : 'PICKUP') as DeliveryType);
                    }
                    setSelectedTime((orderServeDetails?.pickupDetails?.scheduleType === 'ASAP' || orderServeDetails?.deliveryDetails?.scheduleType === 'ASAP') ? 'ASAP' : 'SCHEDULED');

                    let selectedOrderDate = new Date();
                    let selectedOrderTime = new Date();

                    if(orderServeDetails?.pickupDetails?.scheduleType === 'SCHEDULED' && orderServeDetails?.pickupDetails?.pickupAt){
                        setOrderDate(new Date(orderServeDetails?.pickupDetails?.pickupAt));
                        selectedOrderDate = new Date(orderServeDetails?.pickupDetails?.pickupAt);
                        selectedOrderTime = new Date(orderServeDetails?.pickupDetails?.pickupAt);
                    }

                    if(orderServeDetails?.deliveryDetails?.scheduleType === 'SCHEDULED' && orderServeDetails?.deliveryDetails?.deliveryAt){
                        setOrderDate(new Date(orderServeDetails?.deliveryDetails?.deliveryAt));
                        selectedOrderDate = new Date(orderServeDetails?.deliveryDetails?.deliveryAt);
                        selectedOrderTime = new Date(orderServeDetails?.deliveryDetails?.deliveryAt);
                    }

                    const scheduleType = currentOrderDetails?.fullFillType === 'DELIVERY' ? currentOrderDetails?.deliveryDetails?.scheduleType : orderServeDetails?.pickupDetails?.scheduleType;
                    
                    if(scheduleType === 'SCHEDULED'){
                        // Server time rounding to nearest 15 minutes
                        const updatedServerTime = new Date(serverTime);
                        updatedServerTime.setSeconds(0, 0);

                        const roundedServerTime = roundDownToSlot(updatedServerTime, 15);
                        
                        // Check if selected scheduled time is today, revalidate with server time + prep time
                        const scheduleTimeGap = (orderServeDetails?.fullFillType || '') === 'DELIVERY' ? (companyDetails?.deliveryPrepTimeInMinutes || 30) : (companyDetails?.pickupPrepTimeInMinutes || 30); // in minutes
                        
                        const minValidScheduleTime = new Date((roundedServerTime.getTime() + scheduleTimeGap * 60000));
        
                        if(selectedOrderDate.toDateString() === serverTime.toDateString() && selectedOrderDate < minValidScheduleTime){
                            // console.log("Scheduled time is not valid anymore");
                            setOrderTime('');
                            setAllowedTimes([]);
                            setIsTimeSlotsAvailable(false);
                            fetchAvailableTimesDebouncedRef.current();
                            loadingService.loading(false);
        
                            alertService.error(t('serviceModal.selectedTimeNoLongerAvailable'), 5000, true);
                        } else{
                            setOrderTime(selectedOrderTime.toTimeString().substring(0,5)); // HH:mm format
                        }
                    } else{
                        setOrderTime(''); // HH:mm format
                    }

                    loadingService.loading(false);
                }

            } else {
                setShowServiceModal(true);
                loadingService.loading(false);
            }

        }

    }, []);

    useEffect(() => {
        if (
            selectedTime === 'ASAP' &&
            pickupAcceptanceStatus === 'closed' &&
            deliveryAcceptanceStatus === 'closed'
        ) {
            setSelectedTime('SCHEDULED');
        }
    }, [pickupAcceptanceStatus, deliveryAcceptanceStatus]);

    useEffect(() => {
        if (didMountRef.current) return;
        didMountRef.current = true;
        fetchCompanyDetails();
    }, []);

    useEffect(() => {
        if(orderDate && selectedTime === 'SCHEDULED'){
            fetchAvailableTimesDebouncedRef.current.cancel();
            setAllowedTimes([]);
            // setIsTimeSlotsAvailable(false);
            fetchAvailableTimesDebouncedRef.current();
        }

    }, [selectedService]);

    // Generate and set unique identity token for the user if not already set
    useEffect(() => {
        // console.log(identityToken);
        if (!identityToken) {
            // Generate unique identity token only for this user that never be duplicated
            const token = uuid()+'-'+new Date().getTime();

            setIdentityToken(token);
        }
    }, [identityToken, setIdentityToken]);

    const checkTimeAvailability = useCallback( async (checkDateTime: Date) => {
        if(!localCompanyDetails || !orderDate) return;

        const selectedDateTime = new Date(orderDate);
        selectedDateTime.setHours(checkDateTime.getHours(), checkDateTime.getMinutes(), 0, 0);

        loadingService.loading(true);
        const checkAvailResponse: CommonResponse = await submitSets(submitCollection.checkMaxOrderAvailability, {
            locationId: localCompanyDetails.locationId || '',
            scheduledDatetime: selectedDateTime.toISOString(),
            fulfillmentType: selectedService || 'PICKUP',
            scheduleType: selectedTime || 'SCHEDULED'
        }, true, null);

        // console.log(checkAvailResponse);

        if(checkAvailResponse && checkAvailResponse.status && checkAvailResponse.extra){
            const isShopCloseForTheDay = (checkAvailResponse.extra as { isClosedForTheDay: boolean }).isClosedForTheDay;
            const isNoSlotsAvailable = (checkAvailResponse.extra as { isNoSlotsAvailable: boolean }).isNoSlotsAvailable;
            setIsNoSlotsAvailableStatus(isNoSlotsAvailable);
            const isMaxOrderLimitReached = (checkAvailResponse.extra as { isMaxOrderLimitReached: boolean }).isMaxOrderLimitReached;

            if(isMaxOrderLimitReached){
                const nextAvailTime = (checkAvailResponse.extra as { nextAvailTime: Date }).nextAvailTime;
                const nextAvailDate = nextAvailTime ? timezoneDateConvert({
                    utcDate: new Date(nextAvailTime).toISOString(),
                    timezone: (localCompanyDetails?.timeZone || ''),
                    format: 'YYYY-MM-DD'
                }) : null;
                const isNextDateToday = nextAvailTime && new Date(nextAvailDate || '').toDateString() === selectedDateTime.toDateString();

                const latestAvailTime = (checkAvailResponse.extra as { latestAvailTime: Date }).latestAvailTime;
                const latestAvailDate = latestAvailTime ? timezoneDateConvert({
                    utcDate: new Date(latestAvailTime).toISOString(),
                    timezone: (localCompanyDetails?.timeZone || ''),
                    format: 'YYYY-MM-DD'
                }) : null;
                const isLatestDateToday = latestAvailTime && new Date(latestAvailDate || '').toDateString() === selectedDateTime.toDateString();
                
                if(isShopCloseForTheDay){
                    const isDateToday = selectedDateTime.toDateString() === (new Date()).toDateString(); 
                    alertService.error(t('Checkout.ERROR_SCHEDULE_DATE_CLOSED_FOR_DAY', { date: (isDateToday ? 'today' : 'the selected date') }
                    ), 5000, true);
                    setOrderTime('');

                } else if(isNoSlotsAvailable){
                // } else if(isNoSlotsAvailable || (!isNextDateToday && !isLatestDateToday)){
                    // Show error if next available date is not same as selected date
                    const isDateToday = selectedDateTime.toDateString() === (new Date()).toDateString();
                    const method = await getOrderMethod();
                    const dateText = isDateToday ? 'today' : 'the selected date';

                    switch (method) {
                        case 'delivery':
                            alertService.error(t('Checkout.ERROR_DELIVERY_FULLY_BOOKED', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                        case 'pickup':
                            alertService.error(t('Checkout.ERROR_PICKUP_FULLY_BOOKED', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                        case 'both':
                            alertService.error(t('Checkout.ERROR_SCHEDULE_DATE_FULLY_BOOKED', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                        case 'none':
                        default:
                            alertService.error(t('Checkout.ERROR_NO_SERVICE_AVAILABLE', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                    }
                } else{

                    setShowScheduleTimeErrorSheet(true);
                    
                    if(!isNextDateToday){
                        setAvailableScheduleTimes([latestAvailTime]);
                    } else if(!isLatestDateToday){
                        setAvailableScheduleTimes([nextAvailTime]);
                    } else{
                        setAvailableScheduleTimes([latestAvailTime, nextAvailTime]);
                    }
                }
            }
        }
        
        loadingService.loading(false);

    }, [orderTime]);

    useEffect(() => {
        if(orderTime) {
            // If orderTime is a Date object, extract hours and minutes from it
            let hours: number, minutes: number;
            if (orderTime && typeof orderTime === 'object' && orderTime instanceof Date) {
                hours = (orderTime as Date).getHours();
                minutes = (orderTime as Date).getMinutes();
            } else if (typeof orderTime === 'string') {
                [hours, minutes] = orderTime.split(':').map(Number);
            } else {
                hours = 0;
                minutes = 0;
            }
            const selectedDateTime = new Date(orderDate?.getTime() || Date.now());
            selectedDateTime.setHours(hours, minutes, 0, 0);
            
            checkTimeAvailability(selectedDateTime);
        }
    }, [orderTime]);

    // State for allowed times based on opening hours
    const [allowedTimes, setAllowedTimes] = useState<Date[]>([]);
    const [isTimeSlotsAvailable, setIsTimeSlotsAvailable] = useState(true);
    const [isSlotsLoading, setIsSlotsLoading] = useState(true);
    
    /* const roundUpToSlot = (date: Date, gapMinutes: number) => {
        const ms = gapMinutes * 60 * 1000;
        return new Date(Math.ceil(date.getTime() / ms) * ms);
    } */

    const roundDownToSlot = (date: Date, gapMinutes: number) => {
        const ms = gapMinutes * 60 * 1000;
        return new Date(Math.floor(date.getTime() / ms) * ms);
    }

    const getLastAcceptanceTimeToday = useCallback((
        serviceType: SlotType,
        serverTime: Date,
        details: CompanyDetails
    ): Date | null => {
        const currentDay = serverTime.toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as DayOfWeek;

        const reArrangedOpeningHours = rearrangeOpeningHours(
            details?.additionalInfo?.openingHours || [],
            details?.timeZone
        );

        const openingHours = (reArrangedOpeningHours || []).find(
            (day) => day.isOpen && day.day.toLowerCase() === (currentDay as string).substring(0, 3)
        ) as OpeningHour | undefined;

        if (!openingHours) return null;

        const scheduleTimeGap = serviceType === 'DELIVERY'
            ? (details?.deliveryPrepTimeInMinutes || 30)
            : (details?.pickupPrepTimeInMinutes || 30);

        const startOfDay = new Date(serverTime);
        startOfDay.setHours(0, 0, 0, 0);

        const updatedSlots = getAvailableTimeSlotsByType(
            startOfDay,
            openingHours?.openingHours || [],
            (details?.fulfillmentOffSchedule || {}) as TimeRangeConfig,
            serviceType,
            scheduleTimeGap
        );

        if (!updatedSlots || updatedSlots.length === 0) return null;

        let lastAcceptanceTime: Date | null = null;

        updatedSlots.forEach((hourRange) => {
            const convertedCloseTime = timezoneDateConvert({
                utcDate: hourRange?.endTime || '',
                timezone: details?.timeZone,
                format: 'HH:mm'
            });

            const [closeHour, closeMinute] = (convertedCloseTime || '').split(':').map(Number);

            const closeDateTime = new Date(startOfDay);
            closeDateTime.setHours(closeHour, closeMinute, 0, 0);

            const rawCutoff = new Date(closeDateTime.getTime() - scheduleTimeGap * 60000 -1*60000);

            // Round down to nearest 15-min slot boundary, consistent with slot generation elsewhere
            const cutoff = roundDownToSlot(rawCutoff, timeGap);

            if (!lastAcceptanceTime || cutoff > lastAcceptanceTime) {
                lastAcceptanceTime = cutoff;
            }
        });

        return lastAcceptanceTime;
    }, []);

    const manualFetchAvailableTimes = async (checkDate: Date) => {

        const scheduleTimeGap = selectedService === 'DELIVERY'? (localCompanyDetails?.deliveryPrepTimeInMinutes || 30) : (localCompanyDetails?.pickupPrepTimeInMinutes || 30); // in minutes
        const currentDay = new Date(checkDate || '').toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as keyof NonNullable<typeof localCompanyDetails>['additionalInfo']['openingHours'][number]['day'];
        // let isOpenToday = false;
        
        // Calculate allowed times based on current day opening hours
        // const openingHours = (localCompanyDetails?.additionalInfo?.openingHours || []).find((day) => day.isOpen && day.day.toLowerCase() === (currentDay as string).substring(0, 3)) as OpeningHour | undefined;

        // Rearrange opening hours by day and convert to timezone
        const reArrangedOpeningHours = rearrangeOpeningHours(
            localCompanyDetails?.additionalInfo?.openingHours || [],
            localCompanyDetails?.timeZone || ''
        );

        // find current day opening hours
        const openingHours = (reArrangedOpeningHours || []).find((day) => day.isOpen && day.day.toLowerCase() === (currentDay as string).substring(0, 3)) as OpeningHour | undefined;
        // console.log(openingHours);
        
        if (openingHours) {
            //current hour and minute
            let currentTime = new Date((currentDate ? currentDate.toISOString() : new Date().toISOString()));

            loadingService.loading(true);
            setIsSlotsLoading(true);
            const servereTimeResponse: CommonResponse = await submitSets(submitCollection.loadServerTime, {
                locationId: (localCompanyDetails?.locationId || "")
            }, false, null);

            if (
                servereTimeResponse &&
                servereTimeResponse.status &&
                servereTimeResponse.extra &&
                Object.keys(servereTimeResponse.extra).length > 0
            ) {
                const respTimeString = (servereTimeResponse.extra as { serverTime?: string }).serverTime || '';
                currentTime = new Date(timezoneDateConvert({ utcDate: respTimeString, timezone: localCompanyDetails?.timeZone, format: 'YYYY-MM-DD HH:mm:ss' }));
                // console.log("Server time:", currentTime);

                // isOpenToday = (servereTimeResponse.extra as { shopStatus?: string }).shopStatus === 'OPEN';
                setCurrentServerTime(currentTime);

                setCompanyDetails({
                    ...localCompanyDetails,
                    serverTime: currentTime.toISOString(),
                } as CompanyDetails);
            }

            loadingService.loading(false);
            setIsSlotsLoading(false);
            
            // const changeCurrentTime = new Date(currentTime.setMinutes(currentTime.getMinutes() + timeGap));
            const currentHour = currentTime.getHours();
            const currentMinute = currentTime.getMinutes();
            
            // Always set to midnight first to avoid time drift
            const setStartDate = new Date(checkDate?.getTime() || currentTime.getTime());
            setStartDate.setHours(0, 0, 0, 0);

            const times: Date[] = [];
            // Recheck slots with not available delivery/pickup hours
            const updatedOpeningHours: AvailableSlot[] = getAvailableTimeSlotsByType(setStartDate, (openingHours?.openingHours || []), (localCompanyDetails?.fulfillmentOffSchedule || {}) as TimeRangeConfig, selectedService as SlotType,scheduleTimeGap);
            // console.log(updatedOpeningHours);
            // console.log("openingHours",openingHours?.openingHours);

            updatedOpeningHours.sort((a, b) => {
            // (openingHours?.openingHours || []).sort((a, b) => {
                const aStart = timezoneDateConvert({ utcDate: (a.startTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });
                const bStart = timezoneDateConvert({ utcDate: (b.startTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });
                return (aStart < bStart ? -1 : 1);
            }).forEach((hourRange, index) => {
                // openingHours.open and openingHours.close are in 'HH:mm' format, e.g., '11:00', '23:00'
                // Use local time to avoid timezone offset issues
                const convertedOpenTime = timezoneDateConvert({ utcDate: (hourRange?.startTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });
                const convertedCloseTime = timezoneDateConvert({ utcDate: (hourRange?.endTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });

                const [openHour, openMinute] = (convertedOpenTime || '').split(':').map(Number);
                const [closeHour, closeMinute] = (convertedCloseTime || '').split(':').map(Number);

                // If the selected date is today, set the start time to the next available slot
                if (checkDate && new Date(checkDate).toDateString() === new Date(currentTime).toDateString()) {

                    // If current time is before opening
                    // console.log(currentHour, openHour, currentMinute, openMinute);

                    if (index === 0 && (currentHour < openHour || (currentHour === openHour && currentMinute < openMinute))) {
                        // if(!isOnload){
                        //     alertService.info(t('serviceModal.chooseTimeBetween', { openHour: openHour.toString().padStart(2, '0'), openMinute: openMinute.toString().padStart(2, '0') }), 3000, true);
                        // }

                        setStartDate.setHours(openHour, (openMinute + scheduleTimeGap), 0, 0);
                    } else{
                        
                    }

                    if (currentHour > openHour || (currentHour === openHour && currentMinute > openMinute)) {
                        // Round up to the next 15 minute interval
                        const roundedMinutes = Math.floor(currentMinute / 15) * 15;
                        setStartDate.setHours(currentHour, (roundedMinutes + scheduleTimeGap), 0, 0);

                        // If the rounded minutes exceed 60, adjust the hour and minutes accordingly
                        if (roundedMinutes >= 60) {
                            setStartDate.setHours(currentHour + 1, scheduleTimeGap, 0, 0);
                        }
                    } else {
                        setStartDate.setHours(openHour, (openMinute + scheduleTimeGap), 0, 0);
                    }
                } else {
                    setStartDate.setHours(openHour, (openMinute + scheduleTimeGap), 0, 0);
                }
                
                const setEndDate = new Date(checkDate?.getTime() || Date.now());
                setEndDate.setHours(0, 0, 0, 0);
                setEndDate.setHours(closeHour, closeMinute, 0, 0);

                const startTime = new Date(setStartDate.getTime()); // add time gap to start time
                const endTime = new Date((setEndDate.getTime() - scheduleTimeGap * 60000)); // subtract time gap from end time
                
                // Use local time for all time slots
                const slot = roundDownToSlot(startTime, timeGap);
                
                while (slot <= endTime) {
                    times.push(new Date(slot.getTime()));
                    slot.setMinutes(slot.getMinutes() + 15);
                }
                
            });

            setAllowedTimes(times);
            setIsTimeSlotsAvailable(times.length > 0);
        } else{
            setAllowedTimes([]);
            setIsTimeSlotsAvailable(false);
        }
    }

    const fetchAvailableTimes = useCallback(async () => {
        if(!orderDate){
            setAllowedTimes([]);
            setIsTimeSlotsAvailable(false);
            return;
        }

        if (isPreventLoadTimes || !localCompanyDetails || !localCompanyDetails.additionalInfo || !localCompanyDetails.additionalInfo.openingHours) {
            setIsPreventLoadTimes(false);
            return;
        }
        
        const scheduleTimeGap = selectedService === 'DELIVERY'? (localCompanyDetails?.deliveryPrepTimeInMinutes || 30) : (localCompanyDetails?.pickupPrepTimeInMinutes || 30); // in minutes
        const currentDay = new Date(orderDate || '').toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as keyof NonNullable<typeof localCompanyDetails.additionalInfo.openingHours>[number]['day'];
        // let isOpenToday = false;
        
         // Rearrange opening hours by day and convert to timezone
        const reArrangedOpeningHours = rearrangeOpeningHours(
            localCompanyDetails.additionalInfo.openingHours || [],
            localCompanyDetails.timeZone
        );

        // find current day opening hours
        const openingHours = (reArrangedOpeningHours || []).find((day) => day.isOpen && day.day.toLowerCase() === (currentDay as string).substring(0, 3)) as OpeningHour | undefined;
        // console.log(openingHours);
        
        if (openingHours) {
            setIsSelectedDayClosed(false);

            //current hour and minute
            let currentTime = new Date((currentDate ? currentDate.toISOString() : new Date().toISOString()));

            setIsPickerTimesLoading(true);
            loadingService.loading(true);
            setIsSlotsLoading(true);
            
            const servereTimeResponse: CommonResponse = await submitSets(submitCollection.loadServerTime, {
                locationId: (localCompanyDetails?.locationId || "")
            }, false, null);

            if (
                servereTimeResponse &&
                servereTimeResponse.status &&
                servereTimeResponse.extra &&
                Object.keys(servereTimeResponse.extra).length > 0
            ) {
                const respTimeString = (servereTimeResponse.extra as { serverTime?: string }).serverTime || '';
                currentTime = new Date(timezoneDateConvert({ utcDate: respTimeString, timezone: localCompanyDetails.timeZone, format: 'YYYY-MM-DD HH:mm:ss' }));
                // console.log("Server time:", currentTime);

                // isOpenToday = (servereTimeResponse.extra as { shopStatus?: string }).shopStatus === 'OPEN';
                setCurrentServerTime(currentTime);

                setCompanyDetails({
                    ...localCompanyDetails,
                    serverTime: currentTime.toISOString(),
                } as CompanyDetails);
            }

            loadingService.loading(false);
            setIsSlotsLoading(false);
            
            // const changeCurrentTime = new Date(currentTime.setMinutes(currentTime.getMinutes() + timeGap));
            const currentHour = currentTime.getHours();
            const currentMinute = currentTime.getMinutes();
            
            // Always set to midnight first to avoid time drift
            const setStartDate = new Date(orderDate?.getTime() || currentTime.getTime());
            setStartDate.setHours(0, 0, 0, 0);

            const times: Date[] = [];
            let isCurrentlyClosed = false;

            let minStartTime = new Date(openingHours.openingHours[0].startTime);
            let maxEndTime = new Date(openingHours.openingHours[0].endTime);

            // Loop through ALL items starting from index 0
            openingHours.openingHours.forEach((range) => {
                if (new Date(range.startTime) < minStartTime) {
                    minStartTime = new Date(range.startTime);
                }
                if (new Date(range.endTime) > maxEndTime) {
                    maxEndTime = new Date(range.endTime);
                }
            });

            // Recheck slots with not available delivery/pickup hours
            const updatedOpeningHours: AvailableSlot[] = getAvailableTimeSlotsByType(setStartDate, (openingHours?.openingHours || []), (localCompanyDetails?.fulfillmentOffSchedule || {}) as TimeRangeConfig, selectedService as SlotType,scheduleTimeGap);
            // console.log(updatedOpeningHours);
            // console.log("openingHours",openingHours?.openingHours);

            updatedOpeningHours.sort((a, b) => {
            // (openingHours?.openingHours || []).sort((a, b) => {
                const aStart = timezoneDateConvert({ utcDate: (a.startTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });
                const bStart = timezoneDateConvert({ utcDate: (b.startTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });
                return (aStart < bStart ? -1 : 1);
            }).forEach((hourRange, index) => {
                // openingHours.open and openingHours.close are in 'HH:mm' format, e.g., '11:00', '23:00'
                // Use local time to avoid timezone offset issues
                const convertedOpenTime = timezoneDateConvert({ utcDate: (hourRange?.startTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });
                const convertedCloseTime = timezoneDateConvert({ utcDate: (hourRange?.endTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });

                const [openHour, openMinute] = (convertedOpenTime || '').split(':').map(Number);
                const [closeHour, closeMinute] = (convertedCloseTime || '').split(':').map(Number);

                // If the selected date is today, set the start time to the next available slot
                if (orderDate && new Date(orderDate).toDateString() === new Date(currentTime).toDateString()) {

                    // If current time is before opening
                    // console.log(currentHour, openHour, currentMinute, openMinute);

                    if (index === 0 && (currentHour < openHour || (currentHour === openHour && currentMinute < openMinute))) {
                        // if(!isOnload){
                        //     alertService.info(t('serviceModal.chooseTimeBetween', { openHour: openHour.toString().padStart(2, '0'), openMinute: openMinute.toString().padStart(2, '0') }), 3000, true);
                        // }

                        setStartDate.setHours(openHour, (openMinute + scheduleTimeGap), 0, 0);
                    } else{
                         if((index + 1) === updatedOpeningHours.length && (currentHour > closeHour || (currentHour === closeHour && currentMinute >= closeMinute))){
                            if(!isOnload && !isNoSlotsAvailableStatus){
                                alertService.info(t('serviceModal.shopOutOfHours'), 3000, true);
                            }
                            isCurrentlyClosed = true;
                        }
                    }

                    if (currentHour > openHour || (currentHour === openHour && currentMinute > openMinute)) {
                        // Round up to the next 15 minute interval
                        const roundedMinutes = Math.floor(currentMinute / 15) * 15;
                        setStartDate.setHours(currentHour, (roundedMinutes + scheduleTimeGap), 0, 0);

                        // If the rounded minutes exceed 60, adjust the hour and minutes accordingly
                        if (roundedMinutes >= 60) {
                            setStartDate.setHours(currentHour + 1, scheduleTimeGap, 0, 0);
                        }
                    } else {
                        // setStartDate.setHours(openHour, (openMinute + scheduleTimeGap), 0, 0);
                        setStartDate.setHours(openHour, (openMinute), 0, 0);
                    }
                } else {
                    // setStartDate.setHours(openHour, (openMinute + scheduleTimeGap), 0, 0);
                    setStartDate.setHours(openHour, (openMinute), 0, 0);
                }
                
                const setEndDate = new Date(orderDate?.getTime() || Date.now());
                setEndDate.setHours(0, 0, 0, 0);
                setEndDate.setHours(closeHour, closeMinute, 0, 0);

                let startTime = new Date(setStartDate.getTime());
                if (
                    startTime.getHours() === minStartTime.getHours() &&
                    startTime.getMinutes() === minStartTime.getMinutes() &&
                    startTime.getSeconds() === minStartTime.getSeconds()
                ) {
                    startTime = new Date(setStartDate.getTime() + scheduleTimeGap * 60000);
                }

                let endTime = new Date(setEndDate.getTime());
                if (
                    endTime.getHours() === maxEndTime.getHours() &&
                    endTime.getMinutes() === maxEndTime.getMinutes() &&
                    endTime.getSeconds() === maxEndTime.getSeconds()
                ) {
                    endTime = new Date(setEndDate.getTime() - scheduleTimeGap * 60000);
                }

                // Use local time for all time slots
                const slot = roundDownToSlot(startTime, timeGap);
                
                while (slot <= endTime) {
                    times.push(new Date(slot.getTime()));
                    slot.setMinutes(slot.getMinutes() + 15);
                }

                // If the last slot is before endTime and there's still time left, add remaining slots
                const lastSlot = times[times.length - 1];
                if (lastSlot && (endTime.getTime() - lastSlot.getTime()) > 0) {
                    const remainingSlot = new Date(lastSlot.getTime());
                    remainingSlot.setMinutes(remainingSlot.getMinutes() + 15);

                    while (remainingSlot <= endTime) {
                        times.push(new Date(remainingSlot.getTime()));
                        remainingSlot.setMinutes(remainingSlot.getMinutes() + 15);
                    }
                }
            });

            if(isCurrentlyClosed){
                setAllowedTimes([]);
                setIsTimeSlotsAvailable(false);
                setIsPickerTimesLoading(false);
                return;
            }

            setAllowedTimes(times);
            setIsTimeSlotsAvailable(times.length > 0);

            if(!isOnload){
                if(orderTime){
                    setOrderTime(''); // Reset order time when date changes
                }
            }
            
            setIsOnload(false);
            setIsPickerTimesLoading(false);

        } else{
            alertService.warn(t('serviceModal.shopClosedDate', { closedDate: timezoneDateConvert({ utcDate: orderDate.toISOString(), timezone: (localCompanyDetails?.timeZone || ''), format: 'YYYY-MM-DD' }) }), 5000, true);
            setAllowedTimes([]);
            setIsTimeSlotsAvailable(false);
            setIsSelectedDayClosed(true);
        }
    }, [
        orderDate,
        isPreventLoadTimes,
        localCompanyDetails,
        selectedService,
        currentDate,
        isOnload,
        isNoSlotsAvailableStatus,
        t
    ]);

    // Debounce fetchAvailableTimes to avoid excessive calls
    const fetchAvailableTimesLatestRef = useRef(fetchAvailableTimes);
        useEffect(() => {
            fetchAvailableTimesLatestRef.current = fetchAvailableTimes;
    }, [fetchAvailableTimes]);

    // Debounce fetchAvailableTimes to avoid excessive calls.
    const fetchAvailableTimesDebouncedRef = useRef<DebouncedFunc<() => Promise<void>>>(
        debounce(() => fetchAvailableTimesLatestRef.current(), 300)
    );

    useEffect(() => {
        return () => {
        fetchAvailableTimesDebouncedRef.current.cancel();
       };
    }, []);

    useEffect(() => {
        if(!orderDate || !localCompanyDetails) return;

        fetchAvailableTimesDebouncedRef.current();
    }, [orderDate, localCompanyDetails]);

    const handleServiceSelect = (service: DeliveryType) => {
        setSelectedService(service);
    };

    const handleTimeSelect = (time: TimeType) => {
        setSelectedTime(time);
                if (time === 'SCHEDULED') {
                    if(orderDate){
                        fetchAvailableTimesDebouncedRef.current();
                    }
                }
    };

    const handleSaveServeDetails = async () => {
        //validate inputs
        if (!selectedService) {
            alertService.error(t('serviceModal.selectServiceType'), 3000, true);
            return;
        }
        
        if (selectedTime === 'SCHEDULED' && (!orderDate || !orderTime)) {
            alertService.error(t('serviceModal.selectDateTime'), 3000, true);
            return;
        }

        let isNowOpen = false;
        
        loadingService.loading(true);
        const servereTimeResponse: CommonResponse = await submitSets(submitCollection.loadServerTime, {
            locationId: (localCompanyDetails?.locationId || "")
        }, false, null);

        let serverTime = new Date((currentDate ? currentDate : new Date().toISOString()));
        let checkServerTime = new Date((currentDate ? currentDate : new Date().toISOString()));
        if (
            servereTimeResponse &&
            servereTimeResponse.status &&
            servereTimeResponse.extra &&
            Object.keys(servereTimeResponse.extra).length > 0
        ) {
            serverTime = new Date(timezoneDateConvert({ utcDate: (servereTimeResponse.extra as { serverTime?: string }).serverTime || '', timezone: localCompanyDetails?.timeZone || 'UTC', format: 'YYYY-MM-DD HH:mm:ss' }));
            checkServerTime = new Date(timezoneDateConvert({ utcDate: (servereTimeResponse.extra as { serverTime?: string }).serverTime || '', timezone: 'UTC', format: 'YYYY-MM-DD HH:mm:ss' }));
            isNowOpen = (servereTimeResponse.extra && (servereTimeResponse.extra as { shopStatus?: string }).shopStatus === 'OPEN') as boolean;

            setCurrentServerTime(serverTime);
            setCompanyDetails({
                ...localCompanyDetails,
                serverTime: serverTime.toISOString(),
            } as CompanyDetails);
        }

        loadingService.loading(false);
        
        // If company is closed, force to scheduled
        if (!isNowOpen && selectedTime === 'ASAP') {
            alertService.warn(t('serviceModal.shopOutOfHours'), 3000, true);
            return;
        }

        // If scheduled time is selected, ensure date and time are valid
        if (selectedTime === 'SCHEDULED') {
            if (!orderDate) {
                alertService.error(t('serviceModal.selectScheduledDate'), 3000, true);
                return;
            }
            if (!orderTime) {
                alertService.error(t('serviceModal.selectScheduledTime'), 3000, true);
                return;
            }

            // Ensure selected date is not in the past
            const today = serverTime;
            today.setHours(0, 0, 0, 0);
            
            const selectedDate = new Date(orderDate);
            selectedDate.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                alertService.warn(t('serviceModal.scheduledDateInPast'), 3000, true);
                return;
            }
        }

        //merge date and time into a single string
        let orderDateTime: Date = new Date();
        if (orderDate && orderTime) {
            // If orderTime is a Date object, extract hours and minutes from it
            let hours: number, minutes: number;
            if (orderTime && typeof orderTime === 'object' && orderTime instanceof Date) {
                hours = (orderTime as Date).getHours();
                minutes = (orderTime as Date).getMinutes();
            } else if (typeof orderTime === 'string') {
                [hours, minutes] = orderTime.split(':').map(Number);
            } else {
                hours = 0;
                minutes = 0;
            }
            orderDateTime = new Date(orderDate);
            orderDateTime.setHours(hours, minutes, 0, 0);
        }

        if(selectedTime === 'SCHEDULED'){
            // Server time rounding to nearest 15 minutes
            const updateCheckServerTime = new Date(checkServerTime);
            updateCheckServerTime.setSeconds(0, 0);

            const roundedServerTime = roundDownToSlot(updateCheckServerTime, 15);
            
            // Check if selected scheduled time is today, revalidate with server time + prep time
            const scheduleTimeGap = (selectedService === 'DELIVERY' ? (localCompanyDetails?.deliveryPrepTimeInMinutes || 30) : (localCompanyDetails?.pickupPrepTimeInMinutes || 30)); // in minutes
            const minValidScheduleTime = new Date((roundedServerTime.getTime() + scheduleTimeGap * 60000));
    
            // console.log(orderDateTime, minValidScheduleTime);
            
            if(orderDateTime.toDateString() === serverTime.toDateString() && orderDateTime < minValidScheduleTime){
                // console.log("Scheduled time is not valid anymore");
                setOrderTime('');
                setAllowedTimes([]);
                setIsTimeSlotsAvailable(false);
                fetchAvailableTimesDebouncedRef.current();
                loadingService.loading(false);
    
                alertService.error(t('serviceModal.selectedTimeNoLongerAvailable'), 5000, true);
                return;
            }
        }

        if (
            selectedTime === 'SCHEDULED' &&
            orderDate &&
            orderDateTime &&
            new Date(orderDateTime) < new Date(serverTime)
        ) {
            alertService.warn(t('serviceModal.scheduledDateInPast'), 5000, true);
            return;
        }

        loadingService.loading(true);
        // Check max order availability again before saving
        const checkScheduleTime = selectedTime === 'SCHEDULED' ? orderDateTime : new Date();
        const checkAvailResponse: CommonResponse = await submitSets(submitCollection.checkMaxOrderAvailability, {
            locationId: localCompanyDetails?.locationId || '',
            scheduledDatetime: checkScheduleTime?.toISOString(),
            fulfillmentType: selectedService || 'PICKUP',
            scheduleType: selectedTime || 'SCHEDULED'
        }, true, null);
        loadingService.loading(false);

        // console.log(checkAvailResponse);

        if(checkAvailResponse && checkAvailResponse.status && checkAvailResponse.extra){
            const isShopCloseForTheDay = (checkAvailResponse.extra as { isClosedForTheDay: boolean }).isClosedForTheDay;
            const isNoSlotsAvailable = (checkAvailResponse.extra as { isNoSlotsAvailable: boolean }).isNoSlotsAvailable;
            setIsNoSlotsAvailableStatus(isNoSlotsAvailable);
            const isMaxOrderLimitReached = (checkAvailResponse.extra as { isMaxOrderLimitReached: boolean }).isMaxOrderLimitReached;

            if(isMaxOrderLimitReached){
                const nextAvailTime = (checkAvailResponse.extra as { nextAvailTime: Date }).nextAvailTime;
                const nextAvailDate = nextAvailTime ? timezoneDateConvert({
                    utcDate: new Date(nextAvailTime).toISOString(),
                    timezone: (localCompanyDetails?.timeZone || ''),
                    format: 'YYYY-MM-DD'
                }) : null;
                const isNextDateToday = nextAvailTime && new Date(nextAvailDate || '').toDateString() === checkScheduleTime.toDateString();

                const latestAvailTime = (checkAvailResponse.extra as { latestAvailTime: Date }).latestAvailTime;
                const latestAvailDate = latestAvailTime ? timezoneDateConvert({
                    utcDate: new Date(latestAvailTime).toISOString(),
                    timezone: (localCompanyDetails?.timeZone || ''),
                    format: 'YYYY-MM-DD'
                }) : null;
                const isLatestDateToday = latestAvailTime && new Date(latestAvailDate || '').toDateString() === checkScheduleTime.toDateString();
                
                if(isShopCloseForTheDay){
                    const isDateToday = checkScheduleTime.toDateString() === (new Date()).toDateString(); 
                    alertService.error(t('Checkout.ERROR_SCHEDULE_DATE_CLOSED_FOR_DAY', { date: (isDateToday ? 'today' : 'the selected date') }), 5000, true);
                    setOrderTime('');

                } else if(isNoSlotsAvailable){
                // } else if(isNoSlotsAvailable || (!isNextDateToday && !isLatestDateToday)){
                    // Show error if next available date is not same as selected date
                    const isDateToday = checkScheduleTime.toDateString() === (new Date()).toDateString();
                    const method = await getOrderMethod();
                    const dateText = isDateToday ? 'today' : 'the selected date';

                    switch (method) {
                        case 'delivery':
                            alertService.error(t('Checkout.ERROR_DELIVERY_FULLY_BOOKED', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                        case 'pickup':
                            alertService.error(t('Checkout.ERROR_PICKUP_FULLY_BOOKED', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                        case 'both':
                            alertService.error(t('Checkout.ERROR_SCHEDULE_DATE_FULLY_BOOKED', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                        case 'none':
                        default:
                            alertService.error(t('Checkout.ERROR_NO_SERVICE_AVAILABLE', { date: dateText }), 6000, true);
                            setOrderTime('');
                            break;
                    }
                    setOrderTime('');
                } else{

                    setShowScheduleTimeErrorSheet(true);

                    if(!isNextDateToday){
                        setAvailableScheduleTimes([latestAvailTime]);
                    } else if(!isLatestDateToday){
                        setAvailableScheduleTimes([nextAvailTime]);
                    } else{
                        setAvailableScheduleTimes([latestAvailTime, nextAvailTime]);
                    }
                }

                return;
            }
        }
        
        // Prepare the details object
        const details = {
            fullFillType: selectedService,
            pickupDetails: selectedService === 'PICKUP' ? {
                recipient: {
                    displayName: "",
                    emailAddress: "",
                    phoneNumber: "",
                    address: null
                },
                scheduleType: selectedTime,
                pickupAt: selectedTime === 'ASAP' ? null : orderDateTime,
                note: "",
            } : null,
            deliveryDetails: selectedService === 'DELIVERY' ? {
                recipient: {
                    displayName: "",
                    emailAddress: "",
                    phoneNumber: "",
                    address: {
                        addressLine1: "",
                        apartmentNo: "",
                        locality: "", //city
                        administrativeDistrictLevel1: "", //state
                        country: ""
                    }
                },
                scheduleType: selectedTime,
                deliveryAt: selectedTime === 'ASAP' ? null : orderDateTime,
                note: "",
            } : null,
            setAt: new Date(serverTime),
        } as OrderServeDetails;
        
        setOrderServeDetails(details);
        
        if(isManualShow){
            onToggleManualShow();
        } else{
            setShowServiceModal(false);
        }
    };

    const changeTime = (date: Date | null) => {
        // console.log("change time", date);

        if (date) {
            const currentOrderDate = new Date(orderDate?.getTime() || Date.now());
            currentOrderDate.setHours(date.getHours(), date.getMinutes(), 0, 0);

            const timeString = date.toTimeString().substring(0, 5); // HH:mm format
            setOrderTime(timeString);
        } else {
            setOrderTime('');
        }
    };

    const getNextOpenTime = (): Date => {
        const currentTime = new Date((currentDate ? currentDate : new Date().toISOString()));
        const nextOpenTime = new Date(currentTime.setDate(currentTime.getDate() + 1)); // Start with tomorrow
        return nextOpenTime;
    };

    const handleClearCart = () => {
        clearCart();
        setOrderServeDetails(null);
        setIsShowCartClearConfirm(false);
        setIsSchedulePassed(false);

        setTimeout(() => {
            if(pathname.includes('cart') || pathname.includes('checkout')){
                router.push('/menu/all');
            } else{
                setSelectedService('PICKUP');
                setSelectedTime((currentCompanyOpen ? 'ASAP' : 'SCHEDULED'));
                setOrderDate(null);
                setOrderTime('');
                setShowServiceModal(true);
            }    
        }, 300);
    };

    useEffect(() => {
        if (!localCompanyDetails || !currentServerTime) return;
        if (!isManualShow && !showServiceModal) return; // only bother while modal is relevant

        const evaluate = (serviceType: SlotType): AcceptanceStatus => {
            const lastTime = getLastAcceptanceTimeToday(serviceType, currentServerTime, localCompanyDetails);
            if (!lastTime) return 'closed';

            const diffMinutes = (lastTime.getTime() - currentServerTime.getTime()) / 60000;

            if (diffMinutes <= 0) return 'closed';
            if (diffMinutes <= HURRY_UP_GAP_MINUTES) return 'hurry';
            return 'ok';
        };

        setPickupAcceptanceStatus(evaluate('PICKUP' as SlotType));
        setDeliveryAcceptanceStatus(evaluate('DELIVERY' as SlotType));
    }, [localCompanyDetails, currentServerTime, isManualShow, showServiceModal, getLastAcceptanceTimeToday]);

    // console.log(allowedStartTime, allowedEndTime);
    const isBothServicesClosed = pickupAcceptanceStatus === 'closed' && deliveryAcceptanceStatus === 'closed';

    const ranges = (localCompanyDetails?.paymentLimits?.ranges?.ranges || []) as RangeItem[];

    const allowMax = localCompanyDetails?.paymentLimits?.allowDeliveryRanges?.max || 0;
    const allowUnit = localCompanyDetails?.paymentLimits?.allowDeliveryRanges?.unit || 'km';
    const maxDistanceStr = `${allowMax}${allowUnit}`;

    // Sort ascending by rangeEnd, and drop/clip any tiers beyond the actual allowed max distance
    const sortedRanges = [...ranges]
        .sort((a, b) => (a.rangeEnd ?? 0) - (b.rangeEnd ?? 0))
        .filter(r => (r.rangeStart ?? 0) < allowMax)
        .map(r => ({
            ...r,
            rangeEnd: Math.min(r.rangeEnd ?? 0, allowMax) 
        }));

    const buildRangeBreakdown = (): string => {
        if (sortedRanges.length === 0) return '';

        return sortedRanges.map((r, i) => {
            const charge = formatCurrency(r?.amount || 0, r?.currency || 'USD');
            const unit = r?.rangeUnit || 'km';
            const prevEnd = i === 0 ? 0 : sortedRanges[i - 1].rangeEnd || 0;
            const end = r?.rangeEnd || 0;
            const isLast = i === sortedRanges.length - 1;

            if (i === 0) {
                return `${charge} within ${end}${unit}`;
            }
            if (isLast) {
                return `${charge} for ${prevEnd}-${maxDistanceStr}`;
            }
            return `${charge} for ${prevEnd}-${end}${unit}`;
        }).join(', ');
    };

    const rangeBreakdown = buildRangeBreakdown();

    // console.log(minRangeObj, maxRangeObj);

    if(!hasHydrated){
        return null;
    }

    return (<>
        <Modal show={!isOnlineHold && ((!isShowCartClearConfirm && isManualShow) || showServiceModal)} centered className={styles.modal} backdrop="static" keyboard={false}>
            {isManualShow && <button className={styles.closeBtn} onClick={onToggleManualShow}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1L9 9M9 1L1 9" stroke="#101010" strokeWidth="2" strokeLinecap="round"/>
                </svg>
            </button>}
            
            <Modal.Body className={styles.modalBody}>
                <h2>{isFutureScheduleClosed ? t('serviceModal.futureScheduleClosedMessage') : isSchedulePassed ? t('serviceModal.clearCartTitleSchedule') : currentCompanyOpen ? t('serviceModal.chooseService'): t('serviceModal.closedMessage')}</h2>
                <p className={styles.subtitle}>{ isFutureScheduleClosed ? t('serviceModal.futureScheduleClosedSubtitle', { scheduleTime: selectedScheduleTime }) : isSchedulePassed ? t('serviceModal.timePassedMessageSchedule') : currentCompanyOpen ? t('serviceModal.chooseOrderMethod') : t('serviceModal.closedSubtitle')}</p>

                {currentCompanyOpen && (pickupAcceptanceStatus !== 'ok' || deliveryAcceptanceStatus !== 'ok') && (() => {
                    const pickupClosed = pickupAcceptanceStatus === 'closed';
                    const deliveryClosed = deliveryAcceptanceStatus === 'closed';
                    const pickupHurry = pickupAcceptanceStatus === 'hurry';
                    const deliveryHurry = deliveryAcceptanceStatus === 'hurry';
                    <AiOutlineThunderbolt  size={12} />
                    if (pickupClosed && deliveryClosed) {
                        return (
                            <div className={styles.urgencyNoteClosed}>
                                <span className={styles.urgencyNoteClosedText}>{t('serviceModal.shopNoLongerAcceptingOrders')}</span>
                            </div>
                        );
                    }

                    if (pickupHurry && deliveryHurry) {
                        return (
                            <div className={styles.urgencyNoteHurry}>
                                <AiOutlineThunderbolt  size={22} />
                                <span className={styles.urgencyNoteHurryText}>{t('serviceModal.pickupAndDeliveryClosingSoon')}</span>
                            </div>
                        );
                    }

                    if (pickupHurry) {
                        return (
                            <div className={styles.urgencyNoteHurry}>
                                <AiOutlineThunderbolt  size={22} />
                                <span className={styles.urgencyNoteHurryText}>{t('serviceModal.pickupClosingSoon')}</span>
                            </div>
                        );
                    }

                    if (deliveryHurry) {
                        return (
                            <div className={styles.urgencyNoteHurry}>
                                <AiOutlineThunderbolt  size={22} />
                                <span className={styles.urgencyNoteHurryText}>{t('serviceModal.deliveryClosingSoon')}</span>
                            </div>
                        );
                    }

                    return null;
                })()}

                <div className={styles.serviceButtons}>
                    {/* companyDetails.isPickupEnabled &&  */}
                    {isPickupEnabled && <button
                        className={`${styles.serviceButton} ${selectedService === 'PICKUP' ? styles.active : ''}`}
                        onClick={() => {
                            if(localCompanyDetails?.isDeliveryOn){
                                handleServiceSelect('PICKUP');
                            }
                        }}
                        id='pickup-service-button'
                    >
                        <RiRedPacketLine size={20} />
                        <span>{t('serviceModal.pickup')}</span>
                    </button>}

                    {/* companyDetails.isDeliveryEnabled &&  */}
                    {isDeliveryEnabled && <button
                        className={`${styles.serviceButton} ${selectedService === 'DELIVERY' ? styles.active : ''}` + ` ${localCompanyDetails?.isDeliveryOn ? '' : styles.disabled}`}
                        onClick={() => {
                            if(localCompanyDetails?.isDeliveryOn){
                                handleServiceSelect('DELIVERY');
                            }
                        }}
                        id='delivery-service-button'
                    >
                        <TbTruckDelivery size={24} />
                        <span>{t('serviceModal.delivery')}</span>
                    </button>}
                </div>

                {!isCompDetailsLoading && !localCompanyDetails?.isDeliveryOn ? (
                    <span className={styles.nodeliveryNote}>{t('serviceModal.deliveryNotAvailable')}</span>
                ) : !isCompDetailsLoading && selectedTime === 'ASAP' && isDeliveryEnabled && deliveryAcceptanceStatus === 'closed' && pickupAcceptanceStatus !== 'closed' ? (
                    <span className={styles.nodeliveryNote}>{t('serviceModal.deliveryNoLongerAccepted')}</span>
                ) :!isCompDetailsLoading && selectedTime === 'ASAP' && isDeliveryEnabled && deliveryAcceptanceStatus === 'closed' && pickupAcceptanceStatus === 'closed' ? (
                    null
                ) : null}
                
                {currentCompanyOpen && <div className={styles.timeSelector}>
                <button
                    id="asap-time-button"
                    className={`${styles.timeButton} ${selectedTime === 'ASAP' ? styles.active : ''}`}
                    onClick={() => handleTimeSelect('ASAP')}
                >
                    {t('serviceModal.now')}
                </button>
                <button
                    id="scheduled-time-button"
                    className={`${styles.timeButton} ${selectedTime === 'SCHEDULED' ? styles.active : ''}`}
                    onClick={() => handleTimeSelect('SCHEDULED')}
                >
                    {t('serviceModal.later')}
                </button>
                </div>}

                {(!currentCompanyOpen || selectedTime === 'SCHEDULED') && (
                <div className={styles.dateTimeSelector}>
                    <div className={styles.inputGroup}>
                        <label>{t('Checkout.DATE')}</label>
                        <DatePicker
                            ref={pickerRef}
                            id="order-date-picker"
                            selected={orderDate ? new Date(orderDate) : null}
                            disabled={isPickerTimesLoading}
                            onKeyDown={(e) => e.preventDefault()}
                            // onChange={(date: Date | null) => {
                            //     if (date) {
                            //         setOrderDate(new Date(date));
                            //         setIsSlotsLoading(true);
                            //         setOrderTime('');
                            //     } else {
                            //         setOrderDate(null);
                            //     }
                            // }}
                            onSelect={async (date: Date | null) => {
                                if (!date) {
                                    setOrderDate(null);
                                    return;
                                }

                                setIsSlotsLoading(true);
                                setOrderTime('');
                                loadingService.loading(true);

                                const companyResponse = await submitSets(
                                    submitCollection.getCompanyDetails,
                                    { locationId: localCompanyDetails?.locationId || null },
                                    false,
                                    null
                                );

                                if (companyResponse?.status && companyResponse?.extra) {
                                    const freshCompanyDetails = companyResponse.extra as CompanyDetails;
                                    setLocalCompanyDetails(freshCompanyDetails);
                                }

                                const newDate = new Date(date);
                                setOrderDate(prev => (prev && prev.getTime() === newDate.getTime() ? prev : newDate));

                                loadingService.loading(false);
                            }}
                            minDate={isBothServicesClosed || isAfterClose ? new Date(getNextOpenTime()) : (currentServerTime ? currentServerTime : new Date())} // Block today when both services are closed
                            maxDate={new Date(new Date().setDate(new Date().getDate() + 7))} // max 7 days ahead
                            includeDates={availableDates}
                            // filterDate={(date) => !isBothServicesClosed || date.toDateString() !== new Date().toDateString()}
                            dateFormat="yyyy-MM-dd"
                            placeholderText={t('Checkout.SELECT_DATE')}
                            autoComplete='off'
                            shouldCloseOnSelect={true}
                            customInput={<Form.Control 
                                type='text' 
                                id='order-date-input'
                                value={orderDate ? orderDate.toISOString().split('T')[0] : ''} 
                                readOnly 
                                ref={datePickerRef} 
                                inputMode='none'
                                style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                />}
                        />

                        {orderDate && <span className={styles.clearOption} onClick={() => {
                            setOrderDate(null);
                            setOrderTime('');
                            setAllowedTimes([]);
                            setIsTimeSlotsAvailable(false);
                            setIsSelectedDayClosed(false);
                        }} title={t('common.clear')}><FiX /></span>}
                        
                    </div>

                    {isSelectedDayClosed && <p className={styles.closedNote}>{t('serviceModal.closedForTheDay')}</p>}

                    {(!isTimeSlotsAvailable && orderDate && !isSlotsLoading) && <p className={styles.closedNote}>{t('serviceModal.noTimeSlotsAvailable')}</p>}

                    {(!isSelectedDayClosed && orderDate && isTimeSlotsAvailable) && <div className={styles.inputGroup}>
                        <label>{t('Checkout.TIME')}</label>
                        <DatePicker
                            id="order-time-picker"
                            selected={orderTime ? new Date(`1970-01-01T${orderTime}:00`) : null}
                            onKeyDown={(e) => e.preventDefault()}
                            onChange={(date: Date | null) => {
                                changeTime(date);
                            }}
                            onCalendarOpen={() => {
                                if(allowedTimes.length > 0 && !orderTime){
                                    setTimeout(() => {
                                        const list = document.querySelector(".react-datepicker__time-list");
                                        if (list) {
                                            list.scrollTop = 0;
                                        }
                                    }, 50);
                                }
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption={t('Checkout.TIME')}
                            dateFormat="hh:mm aa"
                            placeholderText={t('Checkout.SELECT_TIME')}
                            includeTimes={allowedTimes}
                            autoComplete='off'
                            customInput={<Form.Control 
                                type='text' 
                                value={orderTime ? (typeof orderTime === 'string' ? orderTime : orderTime.toTimeString().substring(0, 5)) : ''}
                                readOnly 
                                ref={timePickerRef} 
                                inputMode='none'
                                style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                />}
                            // excludeTimes={excludedTimes}
                        />

                        {orderTime && <span className={styles.clearOption} onClick={() => setOrderTime('')} title={t('common.clear')}><FiX /></span>}
                    </div>}
                </div>
                )}

                <div id="btn-options" className={styles.btnOptions}>
                    <Button
                        id="fulfill-service-button"
                        className={styles.continueButton}
                        disabled={!selectedService || (selectedTime === 'SCHEDULED' && (!orderDate || !orderTime))}
                        onClick={handleSaveServeDetails}
                    >
                        {currentCompanyOpen ? t('serviceModal.continue') : t('serviceModal.placeFutureOrder')}
                    </Button>
                </div>

                {selectedService && <p className={styles.note}>
                {selectedService === 'DELIVERY' ? 
                    (selectedTime === 'ASAP' ? t('serviceModal.nowDeliveryServiceNote', { 
                        rangeBreakdown,
                        maxDistance: maxDistanceStr,
                        minOrderAmount: formatCurrency(
                            (localCompanyDetails?.paymentLimits?.orderValueDeliveryLimit?.amount || 0),
                            (localCompanyDetails?.paymentLimits?.orderValueDeliveryLimit?.currency || 'USD')
                        ),
                        pickupTime: (localCompanyDetails?.pickupPrepTimeInMinutes || 30),
                        deliveryTime: (localCompanyDetails?.deliveryPrepTimeInMinutes || 30)
                    }) : t('serviceModal.deliveryNote', {
                        rangeBreakdown,
                        maxDistance: maxDistanceStr,
                        minOrderAmount: formatCurrency(
                            (localCompanyDetails?.paymentLimits?.orderValueDeliveryLimit?.amount || 0),
                            (localCompanyDetails?.paymentLimits?.orderValueDeliveryLimit?.currency || 'USD'))
                    })) :
                    (selectedTime === 'ASAP' ? t('serviceModal.nowServiceNote', {
                            pickupTime: (localCompanyDetails?.pickupPrepTimeInMinutes || 30),
                            deliveryTime: (localCompanyDetails?.deliveryPrepTimeInMinutes || 30)
                    }) : t('serviceModal.pickupNote'))}
                </p>}
            </Modal.Body>
        </Modal>

        <Modal show={isShowCartClearConfirm} centered className={styles.modal + ' ' + styles.rescheduleOrdermodal} backdrop="static" keyboard={false}>
            <Modal.Body className={styles.modalBody}>
                <h2 className={styles.modalHeader}>{t((isFutureScheduleClosed ? 'serviceModal.futureScheduleClosedMessage' : isSchedulePassed ? 'serviceModal.clearCartTitleSchedule' : 'serviceModal.clearCartTitle'))}</h2>
                <p className={styles.subtitle}>{t((isFutureScheduleClosed ? 'serviceModal.futureScheduleClearSubtitle' : isSchedulePassed ? 'serviceModal.clearCartMessageSchedule' : 'serviceModal.clearCartMessage'), { scheduleTime: selectedScheduleTime })}</p>
                <div className={styles.btnGroup}>
                    <Button variant="secondary" onClick={() => handleClearCart()}>
                        {t('common.clear')}
                    </Button>
                    <Button variant="danger" onClick={() => {
                        setIsShowCartClearConfirm(false);
                        setShowServiceModal(true);
                    }}>
                        {t('serviceModal.keepAndReschedule')}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>

        {/* Schedule time error message sheet */}
        <ModalWrapperSheet
        show={showScheduleTimeErrorSheet}
            onClose={() => setShowScheduleTimeErrorSheet(false)}
        >
            <div className={cartsuccessstyles.textContent}>
                <h2 className={cartsuccessstyles.title}>{selectedTime === "ASAP" ? t('Checkout.SCHEDULE_TIME_ERROR_TITLE_ASAP') : t('Checkout.SCHEDULE_TIME_ERROR_TITLE')}</h2>
                <p className={cartsuccessstyles.description}>
                    {selectedTime === "ASAP" ?  t('Checkout.SCHEDULE_TIME_ERROR_DESCRIPTION_ASAP') : t('Checkout.SCHEDULE_TIME_ERROR_DESCRIPTION')}
                </p>

                <div className={cartsuccessstyles.suggestedTimes}>
                    {availableScheduleTimes && availableScheduleTimes.length > 0 && availableScheduleTimes.map((time, index) => (
                        <button 
                            key={index} 
                            className={cartsuccessstyles.suggestedTimeButton}
                            onClick={() => {
                                // update scheduled time
                                setSelectedTime('SCHEDULED');
                                setIsPreventLoadTimes(true);
                                setOrderDate(new Date(time));
                                setOrderTime(timezoneDateConvert({ utcDate: time.toString(), timezone: localCompanyDetails?.timeZone || 'UTC', format: 'HH:mm' }));
                                setShowScheduleTimeErrorSheet(false);

                                manualFetchAvailableTimes(new Date(time));
                            }}
                        >
                            {timezoneDateConvert({
                                utcDate: new Date(time).toISOString(),
                                timezone: companyDetails?.timeZone || 'UTC',
                                format: 'hh:mm A'
                            })}
                            <small>({timezoneDateConvert({
                                utcDate: new Date(time).toISOString(),
                                timezone: companyDetails?.timeZone || 'UTC',
                                format: 'YY MMM DD'
                            })})</small>
                        </button>
                    ))}
                </div>

            </div>
            {/* <div className={cartsuccessstyles.buttons}>
                <button 
                    id="schedule-time-error-close-button"
                    className={cartsuccessstyles.confirmButton}
                    onClick={() => setShowScheduleTimeErrorSheet(false)}
                >
                    {t('CancelOrderSheet.CLOSE')}
                </button>
            </div> */}
        </ModalWrapperSheet>

    </>);
};

export default ServiceModal;
